import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getAuthToken, getApiBaseUrl } from '../../services/api';
import { applyPlatformLanguage, normalizePlatformLanguage } from '../../utils/applyLanguage';
import { getImageUrl } from '../../utils/imageUrl';
import './ProfileSettings.css';

const ALGERIA = 'Algeria';

const ALGERIAN_CITIES = [
  { value: 'Adrar', key: 'adrar' },
  { value: 'Aïn Defla', key: 'ainDefla' },
  { value: 'Aïn Témouchent', key: 'ainTemouchent' },
  { value: 'Algiers', key: 'algiers' },
  { value: 'Annaba', key: 'annaba' },
  { value: 'Batna', key: 'batna' },
  { value: 'Béchar', key: 'bechar' },
  { value: 'Béjaïa', key: 'bejaia' },
  { value: 'Béni Abbès', key: 'beniAbbes' },
  { value: 'Biskra', key: 'biskra' },
  { value: 'Blida', key: 'blida' },
  { value: 'Bordj Badji Mokhtar', key: 'bordjBadjiMokhtar' },
  { value: 'Bordj Bou Arréridj', key: 'bordjBouArreridj' },
  { value: 'Bouira', key: 'bouira' },
  { value: 'Boumerdès', key: 'boumerdes' },
  { value: 'Chlef', key: 'chlef' },
  { value: 'Constantine', key: 'constantine' },
  { value: 'Djanet', key: 'djanet' },
  { value: 'Djelfa', key: 'djelfa' },
  { value: 'El Bayadh', key: 'elBayadh' },
  { value: "El M'Ghair", key: 'elMghair' },
  { value: 'El Meniaa', key: 'elMeniaa' },
  { value: 'El Oued', key: 'elOued' },
  { value: 'El Tarf', key: 'elTarf' },
  { value: 'Ghardaïa', key: 'ghardaia' },
  { value: 'Guelma', key: 'guelma' },
  { value: 'Illizi', key: 'illizi' },
  { value: 'In Guezzam', key: 'inGuezzam' },
  { value: 'In Salah', key: 'inSalah' },
  { value: 'Jijel', key: 'jijel' },
  { value: 'Khenchela', key: 'khenchela' },
  { value: 'Laghouat', key: 'laghouat' },
  { value: 'Mascara', key: 'mascara' },
  { value: 'Médéa', key: 'medea' },
  { value: 'Mila', key: 'mila' },
  { value: 'Mostaganem', key: 'mostaganem' },
  { value: "M'Sila", key: 'msila' },
  { value: 'Naâma', key: 'naama' },
  { value: 'Oran', key: 'oran' },
  { value: 'Ouargla', key: 'ouargla' },
  { value: 'Ouled Djellal', key: 'ouledDjellal' },
  { value: 'Oum El Bouaghi', key: 'oumElBouaghi' },
  { value: 'Relizane', key: 'relizane' },
  { value: 'Saïda', key: 'saida' },
  { value: 'Sétif', key: 'setif' },
  { value: 'Sidi Bel Abbès', key: 'sidiBelAbbes' },
  { value: 'Skikda', key: 'skikda' },
  { value: 'Souk Ahras', key: 'soukAhras' },
  { value: 'Tamanrasset', key: 'tamanrasset' },
  { value: 'Tébessa', key: 'tebessa' },
  { value: 'Tiaret', key: 'tiaret' },
  { value: 'Timimoun', key: 'timimoun' },
  { value: 'Tindouf', key: 'tindouf' },
  { value: 'Tipaza', key: 'tipaza' },
  { value: 'Tissemsilt', key: 'tissemsilt' },
  { value: 'Tizi Ouzou', key: 'tiziOuzou' },
  { value: 'Tlemcen', key: 'tlemcen' },
  { value: 'Touggourt', key: 'touggourt' }
];

const ALGERIAN_CITY_VALUES = ALGERIAN_CITIES.map((city) => city.value);

const getStoredProfilePhoto = (user) => user?.profile_image || user?.profileImage || null;

const persistUserProfile = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
  window.dispatchEvent(new CustomEvent('user-profile-updated', { detail: user }));
};

const ProfileSettings = () => {
  const { t, i18n } = useTranslation('user');
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const storedCountry = currentUser.country || '';
  const initialCountry = !storedCountry || /algeria/i.test(storedCountry) ? ALGERIA : storedCountry;
  
  const [formData, setFormData] = useState({
    first_name: currentUser.first_name || currentUser.name?.split(' ')[0] || '',
    last_name: currentUser.last_name || currentUser.name?.split(' ').slice(1).join(' ') || '',
    email: currentUser.email || '',
    phone: currentUser.phone || '',
    country: initialCountry,
    city: currentUser.city || '',
    bio: currentUser.bio || '',
    language: normalizePlatformLanguage(i18n.language || currentUser.language)
  });

  const [profileImage, setProfileImage] = useState(getStoredProfilePhoto(currentUser));
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || 'A';
  };

  useEffect(() => {
    const syncLanguage = (lng) => {
      const nextLang = normalizePlatformLanguage(lng);
      setFormData((prev) => (prev.language === nextLang ? prev : { ...prev, language: nextLang }));
    };

    syncLanguage(i18n.language);
    i18n.on('languageChanged', syncLanguage);
    return () => i18n.off('languageChanged', syncLanguage);
  }, [i18n]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'country') {
        const keepCity = value === ALGERIA && ALGERIAN_CITY_VALUES.includes(prev.city);
        return { ...prev, country: value, city: keepCity ? prev.city : '' };
      }
      return { ...prev, [name]: value };
    });

    if (name === 'language') {
      applyPlatformLanguage(value);
    }
  };

  const translateCountry = (country) => {
    if (country === ALGERIA) {
      return t('user.profileSettings.personalInformation.algeria');
    }
    return country;
  };

  const translateCity = (city) => {
    const match = ALGERIAN_CITIES.find((item) => item.value === city);
    if (!match) return city;
    return t(`user.profileSettings.personalInformation.algerianCities.${match.key}`, { defaultValue: city });
  };

  const cityOptions = formData.city && !ALGERIAN_CITY_VALUES.includes(formData.city)
    ? [{ value: formData.city, key: null }, ...ALGERIAN_CITIES]
    : ALGERIAN_CITIES;

  const getInitialFormData = (user = JSON.parse(localStorage.getItem('user') || '{}')) => {
    const storedCountry = user.country || '';
    const country = !storedCountry || /algeria/i.test(storedCountry) ? ALGERIA : storedCountry;
    return {
      first_name: user.first_name || user.name?.split(' ')[0] || '',
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
      email: user.email || '',
      phone: user.phone || '',
      country,
      city: user.city || '',
      bio: user.bio || '',
      language: normalizePlatformLanguage(i18n.language || user.language)
    };
  };

  const handleCancel = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setFormData(getInitialFormData(user));
    setProfileImage(getStoredProfilePhoto(user));
    setProfileImageFile(null);
    setImageRemoved(false);
    setError(null);
    setSuccess(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t('user.profileSettings.actions.imageSizeError'));
        return;
      }
      setProfileImageFile(file);
      setImageRemoved(false);
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfileImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfileImageFile(null);
    setImageRemoved(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const userId = currentUser.id || currentUser.userId;

      if (!userId) {
        console.error('❌ User ID not found in currentUser:', currentUser);
        setError(t('user.profileSettings.actions.userIdNotFound'));
        setLoading(false);
        return;
      }

      console.log('📤 Updating profile for user ID:', userId);

      let savedImageUrl = getStoredProfilePhoto(currentUser);
      if (profileImageFile) {
        const token = getAuthToken();
        if (!token) throw new Error(t('user.profileSettings.actions.userIdNotFound'));

        const photoData = new FormData();
        photoData.append('avatar', profileImageFile);

        const uploadResponse = await fetch(`${getApiBaseUrl()}/api/user-settings/avatar`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: photoData
        });

        const uploadContentType = uploadResponse.headers.get('content-type') || '';
        const uploadResult = uploadContentType.includes('application/json')
          ? await uploadResponse.json()
          : null;

        if (!uploadResponse.ok) {
          throw new Error(uploadResult?.message || t('user.profileSettings.actions.updateFailed'));
        }

        savedImageUrl = uploadResult?.imageUrl || uploadResult?.user?.profile_image || savedImageUrl;
      }

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        city: formData.city,
        bio: formData.bio,
        language: formData.language
      };

      if (imageRemoved && !profileImageFile) {
        payload.profile_image = null;
        savedImageUrl = null;
      }

      const updatedUserData = await api.put('/api/user-settings/profile', payload);
      const savedUser = updatedUserData?.user || {};

      const updatedUser = {
        ...currentUser,
        ...formData,
        ...savedUser,
        name: `${formData.first_name} ${formData.last_name}`,
        profile_image: savedUser.profile_image ?? savedImageUrl,
        profileImage: savedUser.profile_image ?? savedImageUrl
      };
      persistUserProfile(updatedUser);
      setProfileImage(updatedUser.profile_image);
      setProfileImageFile(null);
      setImageRemoved(false);

      setSuccess(t('user.profileSettings.actions.profileUpdated'));
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || t('user.profileSettings.actions.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-settings">
      <div className="profile-settings-header">
        <h2>{t('user.profileSettings.title')}</h2>
        <p>{t('user.profileSettings.subtitle')}</p>
      </div>

      {error && (
        <div className="profile-error-message">
          <i className="material-icons">error</i>
          {error}
        </div>
      )}

      <div className="profile-settings-content">
        {/* Profile Picture Section */}
        <div className="profile-section">
          <h3>{t('user.profileSettings.profilePicture.title')}</h3>
          <div className="profile-picture-section">
            <div className="profile-picture-preview">
              {profileImage ? (
                <img src={profileImage.startsWith('data:') ? profileImage : (getImageUrl(profileImage) || profileImage)} alt="Profile" />
              ) : (
                <div className="profile-picture-placeholder">
                  {getInitials(formData.first_name, formData.last_name)}
                </div>
              )}
            </div>
            <div className="profile-picture-actions">
              <label htmlFor="profile-settings-photo-upload" className="upload-btn">
                <i className="material-icons">photo_camera</i>
                {t('user.profileSettings.profilePicture.uploadPhoto')}
              </label>
              <input
                id="profile-settings-photo-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              {profileImage && (
                <button
                  type="button"
                  className="remove-btn"
                  onClick={handleRemoveImage}
                >
                  <i className="material-icons">delete</i>
                  {t('user.profileSettings.profilePicture.remove')}
                </button>
              )}
            </div>
            <p className="profile-picture-hint">
              {t('user.profileSettings.profilePicture.hint')}
            </p>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="profile-section">
          <h3>{t('user.profileSettings.personalInformation.title')}</h3>
          <form onSubmit={handleSaveProfile} className="profile-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">{t('user.profileSettings.personalInformation.firstName')}</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('user.profileSettings.personalInformation.firstNamePlaceholder')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="last_name">{t('user.profileSettings.personalInformation.lastName')}</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('user.profileSettings.personalInformation.lastNamePlaceholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">{t('user.profileSettings.personalInformation.emailAddress')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={t('user.profileSettings.personalInformation.emailPlaceholder')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t('user.profileSettings.personalInformation.phoneNumber')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('user.profileSettings.personalInformation.phonePlaceholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="country">{t('user.profileSettings.personalInformation.country')}</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country || ALGERIA}
                  onChange={handleInputChange}
                >
                  {formData.country && formData.country !== ALGERIA && (
                    <option value={formData.country}>{formData.country}</option>
                  )}
                  <option value={ALGERIA}>{translateCountry(ALGERIA)}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="city">{t('user.profileSettings.personalInformation.city')}</label>
                <select
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                >
                  <option value="">
                    {t('user.profileSettings.personalInformation.cityPlaceholder')}
                  </option>
                  {cityOptions.map((city) => (
                    <option key={city.key || city.value} value={city.value}>
                      {translateCity(city.value)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="bio">{t('user.profileSettings.personalInformation.bio')}</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows="4"
                placeholder={t('user.profileSettings.personalInformation.bioPlaceholder')}
                maxLength={500}
              />
              <span className="char-count">{t('user.profileSettings.personalInformation.charCount', { count: formData.bio.length })}</span>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={handleCancel}
              >
                {t('user.profileSettings.actions.cancel')}
              </button>
              <button
                type="submit"
                className="save-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="material-icons rotating">refresh</i>
                    {t('user.profileSettings.actions.saving')}
                  </>
                ) : (
                  <>
                    <i className="material-icons">save</i>
                    {t('user.profileSettings.actions.saveChanges')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Preferences Section */}
        <div className="profile-section">
          <h3>{t('user.profileSettings.preferences.title')}</h3>
          <div className="preferences-form">
            <div className="form-group language-field">
              <label htmlFor="language">{t('user.profileSettings.preferences.language')}</label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
              >
                <option value="en">{t('language.english', { ns: 'common' })}</option>
                <option value="fr">{t('language.french', { ns: 'common' })}</option>
                <option value="ar">{t('language.arabic', { ns: 'common' })}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {success && (
        <div className="profile-save-toast" role="status" aria-live="polite">
          <i className="material-icons">check_circle</i>
          {success}
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;

